import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import useRelatedToursData from "@/utils/queries/hooks/useRelatedTours";
import styles from "@/styles/detail/DetailRecap.module.scss";
import useSticky from "@/hooks/useSticky";
import { TourResponseTO } from "@/models/detail";
import { useShouldRenderArrayFC } from "@/hooks/useShouldRenderArrayFC";
import { EDetailSection, ERoomType, ETypePackage } from "@/models/common/enums";
import { useIsMD } from "@/hooks/useWindowSizes";
import { useRouter } from "next/router";
import { getOriginalAmount } from "@/store/checkout/selector-checkout";
import { reviewsContainer } from "@/utils/constants";
import useHomeData from "@/utils/queries/hooks/useHomeData";
import _ from "lodash";
import { viewItem } from "@/utils/analytics/tagmanager";
import TagManager from "react-gtm-module";
import adapterTourViewItemGAnalytics from "@/models/tour";
import KeepInTouch from "../common/KeepInTouch";
import TikTokSection from "../home/TikTokSection";
import InterestedIn from "./InterestedIn";
import PackageDetailHead from "./PackageDetailHead";
import WhyChoosePlay from "./WhyChoosePlay";
import PackageDetailRecap from "./PackageDetailRecap";
import PackageDetailBooking from "./DetailBooking/PackageDetailBooking";
import TravelExperiencesStep from "./TravelExperiencesStep";
import Brochures from "../home/brochures/Brochures";
import OvernightAccommodations from "./OvernightAccomodations/OvernightAccomodations";
import PersonalizeJourneyBanner from "./PersonalizeJourneyBanner";
import WhyPlayItaly from "./WhyPlayItaly";
import useVideoReviewData from "../../utils/queries/hooks/useVideoReviewData";
import { useStore } from "../../store";
import PackageDetailNavbar from "./PackageDetailNavbar";

const PackageDetailBody: FC<{
    packageDetail: TourResponseTO;
    initPremium: boolean;
}> = ({ packageDetail: initialPackageDetail, initPremium }) => {
    const dispatch = useStore(state => state.dispatchPackage);
    const currentSection = useStore(state => state.packageState.currentSection);
    const dispatchCheckout = useStore(state => state.dispatchCheckout);
    const originalAmount = useStore(getOriginalAmount);
    const [isPremium, setIsPremium] = useState<boolean>(initPremium);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const [isPromo, setIsPromo] = useState<boolean>(false);
    const travelExperiencesStepSection = useRef<HTMLDivElement>(null);
    const accommodationsSection = useRef<HTMLDivElement>(null);
    const generalRef = useRef<HTMLDivElement>(null);
    const hiwRef = useRef<HTMLDivElement>(null);
    const { data: videoReviews } = useVideoReviewData();
    const { data: homeData } = useHomeData();

    // Il backend restituisce già i prezzi convertiti in base all'header X-Currency
    // Non serve conversione client-side perché quando cambia valuta la pagina si ricarica
    const packageDetail = initialPackageDetail;

    const relatedTours = useRelatedToursData(
        packageDetail?.quality.tourId,
        isPremium
    );
    const [typePackage, setTipePackage] = useState<ETypePackage>();
    const [areHotelsAvailable, setAreHotelsAvailable] =
        useState<boolean>(false);

    const viewItemQualitySent = useRef(false);
    const viewItemPremiumSent = useRef(false);

    const getPackagePremiumQuality = useCallback(() => {
        if (!packageDetail) return;

        if (packageDetail.onlyQuality) {
            setIsPremium(false);
            setTipePackage(ETypePackage.ONLY_QUALITY);
            return;
        }
        if (packageDetail.onlyPremium) {
            setIsPremium(true);
            setTipePackage(ETypePackage.ONLY_PREMIUM);
            return;
        }
        setTipePackage(ETypePackage.QUALITY_AND_PREMIUM);
    }, [packageDetail, setIsPremium]);

    const getCurrentPackage = useCallback(
        () => (isPremium ? packageDetail?.premium : packageDetail?.quality),
        [isPremium, packageDetail]
    );

    const isVideoAvailable = (): boolean => {
        const currentPackage = getCurrentPackage();
        return !!(
            currentPackage?.coverVideo &&
            !currentPackage.coverVideo.endsWith("/")
        );
    };

    const sortedItineraries = useMemo(() => {
        const currentPackage = getCurrentPackage();
        if (
            !currentPackage?.itineraries ||
            !Array.isArray(currentPackage.itineraries)
        ) {
            return [];
        }
        return [...currentPackage.itineraries].sort(
            (first, second) => +first.day - +second.day
        );
    }, [getCurrentPackage]);

    useEffect(() => {
        dispatchCheckout({
            type: "SET_CLICKDATE",
            payload: false,
        });

        dispatchCheckout({
            type: "SET_CLICKLANGUAGE",
            payload: true,
        });
        dispatchCheckout({
            type: "SET_CLICKCONFIRMDATE",
            payload: false,
        });
    }, [dispatchCheckout]);

    useEffect(() => {
        dispatchCheckout({
            type: "SET_COUPON",
            payload: {
                code: "",
                value: 0,
            },
        });

        dispatchCheckout({
            type: "SET_TOTAL_AMOUNT",
            payload: originalAmount,
        });
    }, [dispatchCheckout, originalAmount]);

    const handleNavigation = (section: EDetailSection) => {
        let currentSectionOffsetTop = null;
        if (section === EDetailSection.GENERAL) {
            currentSectionOffsetTop =
                (generalRef.current?.offsetTop ?? 180) - 180;
        } else if (section === EDetailSection.ITINERARY) {
            let pixelToRemove = 240;
            if (currentSection !== EDetailSection.GENERAL) {
                pixelToRemove = 180;
            }
            currentSectionOffsetTop =
                (travelExperiencesStepSection.current?.offsetTop ?? 180) -
                pixelToRemove;
        } else if (section === EDetailSection.ACCOMMODATION) {
            let pixelToRemove = 280;
            if (currentSection !== EDetailSection.GENERAL) {
                pixelToRemove = 180;
            }
            currentSectionOffsetTop =
                (accommodationsSection.current?.offsetTop ?? 180) -
                pixelToRemove;
        } else if (section === EDetailSection.HIW) {
            currentSectionOffsetTop = (hiwRef.current?.offsetTop ?? 180) - 180;
        }

        dispatch({
            type: "SET_CURRENT_SECTION",
            payload: section,
        });

        window.scrollTo({
            top: currentSectionOffsetTop ?? 180,
            behavior: "smooth",
        });
    };

    const router = useRouter();

    useEffect(() => {
        dispatchCheckout({
            type: "SET_ROOMS_AND_GENERATE_TRAVELERS",
            payload: [
                {
                    type: ERoomType.DOUBLE,
                    adults: 1,
                    children: [],
                    newborns: 0,
                },
            ],
        });
    }, [dispatchCheckout, router]);

    useEffect(() => {
        if (!packageDetail) return;

        getPackagePremiumQuality();
        dispatchCheckout({
            type: "SET_TOUR_DETAIL",
            payload: isPremium ? packageDetail.premium : packageDetail.quality,
        });
    }, [packageDetail, isPremium, dispatchCheckout, getPackagePremiumQuality]);

    useEffect(() => {
        if (!packageDetail) return;
        if (
            isPremium &&
            packageDetail.premium &&
            !viewItemPremiumSent.current
        ) {
            const prop = adapterTourViewItemGAnalytics(packageDetail, true);
            const premiumItem = prop.items.find(
                item => item.item_variant === "premium"
            );
            if (premiumItem) {
                const startingPrice = Number(
                    packageDetail.premium.startingPrice
                );
                premiumItem.price = startingPrice;

                const event = viewItem({
                    currency: "EUR",
                    value: startingPrice,
                    items: [premiumItem],
                });

                TagManager.dataLayer({ dataLayer: event[0] });
                viewItemPremiumSent.current = true;
            }
        } else if (
            !isPremium &&
            packageDetail.quality &&
            !viewItemQualitySent.current
        ) {
            const prop = adapterTourViewItemGAnalytics(packageDetail, false);
            const qualityItem = prop.items.find(
                item => item.item_variant === "quality"
            );
            if (qualityItem) {
                const startingPrice = Number(
                    packageDetail.quality.startingPrice
                );
                qualityItem.price = startingPrice;

                const event = viewItem({
                    currency: "EUR",
                    value: startingPrice,
                    items: [qualityItem],
                });

                TagManager.dataLayer({ dataLayer: event[0] });
                viewItemQualitySent.current = true;
            }
        }
    }, [isPremium, packageDetail]);

    const handleHashChange = useCallback(
        (event?: HashChangeEvent) => {
            const hashToSearch =
                router.asPath.split("#")[1] ?? event?.newURL.split("#")[1];
            if (hashToSearch && hashToSearch === reviewsContainer.hash) {
                const tourReviewsEl = document.getElementById(
                    reviewsContainer.id
                );
                if (tourReviewsEl) {
                    setTimeout(() => {
                        const offsetTop = tourReviewsEl.offsetTop - 250;
                        window.scrollTo({
                            top: offsetTop,
                        });
                    }, 1500);
                }
            }
        },
        [router.asPath]
    );

    useEffect(() => {
        handleHashChange();
        window.addEventListener("hashchange", event => handleHashChange(event));
        return () => {
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, [router.asPath, handleHashChange]);

    useEffect(() => {
        if (!packageDetail) return;

        const nightDestinations = _.merge(
            packageDetail?.premium?.nightDestinations,
            packageDetail?.quality?.nightDestinations
        );

        if (!Array.isArray(nightDestinations)) {
            setAreHotelsAvailable(false);
            return;
        }

        setAreHotelsAvailable(
            !!nightDestinations
                .filter(nightDestination => nightDestination.showHotel)
                .map(nightDestination => nightDestination.hotels).length
        );
    }, [
        packageDetail?.premium?.nightDestinations,
        packageDetail?.quality?.nightDestinations,
    ]);

    const { sticky, stickyRef } = useSticky();

    // Chiamare tutti gli hooks prima dei return condizionali
    const isMD = useIsMD();

    const shouldRenderRelatedTours = useShouldRenderArrayFC(relatedTours);

    // Verifica che packageDetail sia valido prima di renderizzare
    if (!packageDetail || (!packageDetail.premium && !packageDetail.quality)) {
        return null;
    }

    const currentPackage = getCurrentPackage();
    if (!currentPackage) {
        return null;
    }

    return (
        <div
            className="minWidth100vwFromXL"
            style={{ backgroundColor: "rgba(245, 245, 245, 1)" }}
        >
            <PackageDetailHead
                packageDetail={currentPackage}
                days={sortedItineraries.at(-1)?.day ?? "1"}
                isPromo={isPromo}
                isPremium={isPremium}
                setIsPremium={setIsPremium}
                typePackage={typePackage ?? ETypePackage.QUALITY_AND_PREMIUM}
                showAccomodations={
                    !packageDetail.requestAQuote && areHotelsAvailable
                }
                isVideoAvailable={isVideoAvailable()}
            />
            {!isMD && (
                <PackageDetailRecap
                    handleNavigation={handleNavigation}
                    showAccomodations={
                        !packageDetail.requestAQuote && areHotelsAvailable
                    }
                    isRequestAQuote={packageDetail.requestAQuote}
                />
            )}
            {isMD && (
                <div
                    ref={stickyRef}
                    className={`w-100 ${styles.navContainer} ${
                        sticky && styles.isSticky
                    }`}
                >
                    <PackageDetailNavbar
                        showAccomodations={
                            !packageDetail.requestAQuote && areHotelsAvailable
                        }
                        isRequestAQuote={packageDetail.requestAQuote}
                        handleNavigation={handleNavigation}
                    />
                </div>
            )}
            {packageDetail.premium && packageDetail.quality && (
                <PackageDetailBooking
                    ref={generalRef}
                    days={sortedItineraries.at(-1)?.day ?? "1"}
                    packageDetail={currentPackage}
                    isPremium={isPremium}
                    setIsPremium={setIsPremium}
                    typePackage={
                        typePackage ?? ETypePackage.QUALITY_AND_PREMIUM
                    }
                    pricePremium={+packageDetail.premium.price}
                    priceQuality={+packageDetail.quality.price}
                    packagePremium={packageDetail.premium}
                    packageQuality={packageDetail.quality}
                    isRequestAQuote={packageDetail.requestAQuote}
                />
            )}
            <PersonalizeJourneyBanner />
            <WhyPlayItaly />
            <TravelExperiencesStep
                ref={travelExperiencesStepSection}
                itineraries={sortedItineraries}
                isPremium={isPremium}
                // setIsPremium={setIsPremium}
                // typePackage={typePackage!}
                packageDetail={currentPackage}
            />
            <OvernightAccommodations
                ref={accommodationsSection}
                nightDestinations={currentPackage.nightDestinations}
                isPremium={isPremium}
                setIsPremium={setIsPremium}
                typePackage={typePackage ?? ETypePackage.QUALITY_AND_PREMIUM}
                showAccomodations={
                    !packageDetail.requestAQuote && areHotelsAvailable
                }
            />
            {videoReviews && (
                <TikTokSection
                    videoReviews={videoReviews}
                    wordingsDelta={homeData?.staticMedia.videoReviews}
                />
            )}
            <WhyChoosePlay ref={hiwRef} />
            {shouldRenderRelatedTours && relatedTours && (
                <InterestedIn content={relatedTours} />
            )}
            <Brochures />
            <KeepInTouch />
        </div>
    );
};

export default PackageDetailBody;
